'use client';

import {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  type ReactNode,
} from 'react';
import { Box, VStack, HStack, token } from '@terra/ui-styled-system';
import { Text } from '../../components/styled/text';
import { Badge, BadgeVulnerability } from '../../components/badge';
import { SignalStatusBadge } from '../signals';
import { Collapsible } from '../../components/collapsible';
import { Scroll } from '../Scroll';
import { MarkdownPreview } from '../markdown-preview';
import { getBlockedExecutionCopy } from '../chat/trace-view/utils/blocked-execution-copy';
import {
  AlertCircleIcon,
  ArrowRight02Icon,
  UnavailableIcon,
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  FileCodeIcon,
  FileQuestionMarkIcon,
  File01Icon,
  Loading03Icon,
  Edit03Icon,
  CancelCircleIcon,
  Target03Icon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { TraceViewHeader } from '../chat/trace-view/TraceViewHeader';
import { type TodoItem } from '../chat/trace-view/TracePlan';
import { useArtifactsOptional } from '../chat/trace-view/ArtifactsProvider';
import { RecordingDialogButton } from '../chat/trace-view/components/RecordingDialogButton';
import { useAddToEvidence } from '../chat/trace-view/AddToEvidenceProvider';
import { Tooltip } from '../../components/tooltip';
import { toasterApi } from '../toast/ToastProvider';
import { RichMarkdownEditor } from '../rich-markdown-editor';
import { EvidenceCard } from './EvidenceCard';
import { renderProtocolEvidenceCard } from './trace-protocol-evidence';
import { EvidenceCardWithArtifact } from './EvidenceCardWithArtifact';
import { EvidenceCardWithData } from './EvidenceCardWithData';
import { EvidenceCardWithHar } from './EvidenceCardWithHar';
import { EvidenceCardMissing } from './EvidenceCardMissing';
import { ConfirmationDialog } from '../confirmation-dialog';
import {
  type TraceEvidence,
  type TraceConfidence,
  type TraceMetadata,
  type SignalLabel,
  mapLabelToVulnerabilityType,
  getLabelDisplayText,
} from './trace-types';
import type { SignalStatusType } from '@terra/shared-schemas-orchestrator';

export type PlanItem = TodoItem;
export type TraceExpandedSections = Record<string, boolean>;
/** Ref handle for TraceSimplifiedView to access scroll element */
export type TraceSimplifiedViewHandle = {
  /** Get the scroll container element */
  getScrollElement: () => HTMLDivElement | null;
};
export type TraceSimplifiedViewProps = {
  /** Signal/Signal information */
  signal?: {
    id?: string;
    description?: string;
    category?: string;
    analysis?: string;
    stage?: string;
    status?: SignalStatusType | null;
  };
  /** Test plan items */
  plan?: PlanItem[];
  /** Post-attack metadata (analysis and evidence) */
  metadata?: TraceMetadata;
  /** Agent name for context */
  agentName?: string;
  /** Maximum height for the view */
  maxHeight?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Execution status */
  status?: 'running' | 'completed' | 'error' | 'aborted';
  /** Controlled expanded sections state for preserving view state */
  expandedSections?: TraceExpandedSections;
  /** Callback when expanded sections change */
  onExpandedSectionsChange?: (sections: TraceExpandedSections) => void;
  disableDetailedView?: boolean;
};
type AccordionSectionProps = {
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};
type SectionHeaderIconProps = {
  icon: React.ReactNode;
  isExpanded: boolean;
  isHovered: boolean;
};

function renderSignalLabelBadge(label: SignalLabel): React.ReactNode {
  const vulnerabilityType = mapLabelToVulnerabilityType(label);

  if (vulnerabilityType) {
    return (
      <BadgeVulnerability type={vulnerabilityType} size="sm">
        {getLabelDisplayText(label)}
      </BadgeVulnerability>
    );
  }

  const isBlocked = label === 'BLOCKED';
  const isDuplicate = label === 'DUPLICATE';

  return (
    <Badge
      size="sm"
      variant="emphasis"
      bg={isBlocked ? token('colors.purple.3') : token('colors.yellow.3')}
      borderColor={
        isBlocked ? token('colors.purple.6') : token('colors.yellow.6')
      }
      icon={
        isBlocked ? (
          <UnavailableIcon size={12} color={token('colors.purple.11')} />
        ) : isDuplicate ? (
          <Copy01Icon size={12} color={token('colors.yellow.11')} />
        ) : undefined
      }
    >
      {getLabelDisplayText(label)}
    </Badge>
  );
}

function SectionHeaderIcon({
  icon,
  isExpanded,
  isHovered,
}: SectionHeaderIconProps) {
  return (
    <Box color="fg.muted" display="flex" alignItems="center">
      {isHovered ? (
        isExpanded ? (
          <ArrowDown01Icon size={14} />
        ) : (
          <ArrowRight01Icon size={14} />
        )
      ) : (
        icon
      )}
    </Box>
  );
}

function AccordionSection({
  icon,
  title,
  badge,
  isExpanded,
  onToggle,
  children,
}: AccordionSectionProps) {
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const handleHeaderMouseEnter = () => {
    setIsHeaderHovered(true);
  };

  const handleHeaderMouseLeave = () => {
    setIsHeaderHovered(false);
  };

  return (
    <Collapsible.Root open={isExpanded} onOpenChange={() => onToggle()}>
      <Box
        w="full"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        overflow="hidden"
      >
        <Collapsible.Trigger asChild>
          <HStack
            gap={2}
            px={3}
            py={2.5}
            cursor="pointer"
            bg="bg.muted"
            _hover={{ bg: 'bg.hover' }}
            justifyContent="space-between"
            w="full"
            onMouseEnter={handleHeaderMouseEnter}
            onMouseLeave={handleHeaderMouseLeave}
          >
            <HStack gap={2} alignItems="center">
              <SectionHeaderIcon
                icon={icon}
                isExpanded={isExpanded}
                isHovered={isHeaderHovered}
              />
              <Text fontSize="sm" fontWeight="medium" color="fg.default">
                {title}
              </Text>
            </HStack>
            {badge}
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box p={3} bg="bg.default" w="full">
            {children}
          </Box>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}

type EvidenceActionButtonsProps = {
  artifactId: string;
  onEdit: (artifactId: string) => void;
};

function EvidenceActionButtons({
  artifactId,
  onEdit,
}: EvidenceActionButtonsProps) {
  const tEvidence = useTranslations('designSystem.artifacts.evidenceActions');
  const evidenceActions = useAddToEvidence();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);

  if (!evidenceActions) {
    return null;
  }

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isSubmitting) return;
    onEdit(artifactId);
  };

  const handleRemoveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isSubmitting) return;
    setIsRemoveDialogOpen(true);
  };

  const handleRemove = async () => {
    setIsSubmitting(true);

    try {
      await evidenceActions.remove(artifactId);
      toasterApi.success({ title: tEvidence('removed') });
      setIsRemoveDialogOpen(false);
    } catch (error) {
      console.error('Failed to remove evidence', error);
      toasterApi.error({ title: tEvidence('removeFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <HStack gap={0.5} alignItems="center" color="fg.muted">
        <Tooltip title={tEvidence('edit')}>
          <Box
            as="button"
            aria-label={tEvidence('edit')}
            onClick={handleEdit}
            aria-disabled={isSubmitting}
            h="5"
            w="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            opacity={isSubmitting ? 0.5 : 1}
            color="fg.muted"
            borderRadius="xs"
            _hover={{
              color: 'fg.default',
              bg: 'bg.subtle',
            }}
          >
            <Edit03Icon size={12} />
          </Box>
        </Tooltip>
        <Tooltip title={tEvidence('remove')}>
          <Box
            as="button"
            aria-label={tEvidence('remove')}
            onClick={handleRemoveClick}
            aria-disabled={isSubmitting}
            h="5"
            w="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            opacity={isSubmitting ? 0.5 : 1}
            color="fg.muted"
            borderRadius="xs"
            _hover={{
              color: 'fg.default',
              bg: 'bg.subtle',
            }}
          >
            <Delete02Icon size={12} />
          </Box>
        </Tooltip>
      </HStack>
      <ConfirmationDialog
        isOpen={isRemoveDialogOpen}
        onOpenChange={({ open }) => setIsRemoveDialogOpen(open)}
        onConfirm={handleRemove}
        loading={isSubmitting}
        variant="destructive"
        title={tEvidence('removeConfirmTitle')}
        description={tEvidence('removeConfirmDescription')}
        confirmText={tEvidence('removeConfirm')}
        cancelText={tEvidence('cancelRemove')}
      />
    </>
  );
}

function EvidenceDescriptionEditor({
  artifactId,
  initialValue,
  onCancel,
}: {
  artifactId: string;
  initialValue: string;
  onCancel: () => void;
}) {
  const tEvidenceEditor = useTranslations(
    'designSystem.findings.structuredPocEditor',
  );
  const tCommon = useTranslations('designSystem.common');
  const evidenceActions = useAddToEvidence();
  const [value, setValue] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSave = async () => {
    if (isSubmitting) return;
    if (!evidenceActions) return;
    const trimmed = value.trim();

    if (!trimmed) {
      toasterApi.error({ title: 'Evidence description is required' });

      return;
    }

    setIsSubmitting(true);

    try {
      await evidenceActions.update(artifactId, trimmed);
      toasterApi.success({ title: 'Evidence updated' });
      onCancel();
    } catch {
      toasterApi.error({ title: 'Failed to update evidence' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      w="full"
      borderWidth="1px"
      borderColor="orange.6"
      borderRadius="md"
      overflow="hidden"
      bg="bg.canvas"
    >
      <HStack
        px={2}
        h="36px"
        bg="bg.subtle"
        justifyContent="space-between"
        borderBottomWidth="1px"
        borderBottomColor="orange.6"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {tEvidenceEditor('explanation')}
        </Text>
        <HStack gap={0.5}>
          <Box
            as="button"
            aria-label={tCommon('cancel')}
            onClick={() => {
              if (!isSubmitting) {
                onCancel();
              }
            }}
            aria-disabled={isSubmitting}
            h="5"
            w="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            opacity={isSubmitting ? 0.5 : 1}
            color="fg.muted"
            borderRadius="xs"
            _hover={{
              color: 'fg.default',
              bg: 'bg.subtle',
            }}
          >
            <Cancel01Icon size={12} />
          </Box>
          <Box
            as="button"
            aria-label={tCommon('save')}
            onClick={handleSave}
            aria-disabled={isSubmitting}
            h="5"
            w="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
            opacity={isSubmitting ? 0.5 : 1}
            color="fg.muted"
            borderRadius="xs"
            _hover={{
              color: 'fg.default',
              bg: 'bg.subtle',
            }}
          >
            <Tick01Icon size={12} />
          </Box>
        </HStack>
      </HStack>
      <Box p={3}>
        <RichMarkdownEditor
          value={value}
          onChange={setValue}
          height={180}
          minHeight={120}
          maxHeight={400}
          placeholder={tEvidenceEditor('explanationPlaceholder')}
          preview="edit"
          resizable
        />
      </Box>
    </Box>
  );
}

function EvidenceList({
  evidences,
  editingArtifactId,
  onEditEvidence,
  onCancelEdit,
}: {
  evidences: TraceEvidence[];
  editingArtifactId: string | null;
  onEditEvidence: (artifactId: string) => void;
  onCancelEdit: () => void;
}) {
  const artifactsCtx = useArtifactsOptional();

  return (
    <VStack gap={3} alignItems="stretch">
      {evidences.map((evidence, index) => (
        <Box key={`evidence-${index}`}>
          {renderEvidenceCard({
            evidence,
            index,
            artifactsCtx,
            editingArtifactId,
            onEditEvidence,
            onCancelEdit,
          })}
        </Box>
      ))}
    </VStack>
  );
}

function renderEvidenceCard({
  evidence,
  index,
  artifactsCtx,
  editingArtifactId,
  onEditEvidence,
  onCancelEdit,
}: {
  evidence: TraceEvidence;
  index: number;
  artifactsCtx: ReturnType<typeof useArtifactsOptional>;
  editingArtifactId: string | null;
  onEditEvidence: (artifactId: string) => void;
  onCancelEdit: () => void;
}): ReactNode {
  const isEditing =
    !!evidence.artifactId && editingArtifactId === evidence.artifactId;
  const recordingButton = evidence.evidenceId ? (
    <RecordingDialogButton toolCallId={evidence.evidenceId} />
  ) : null;
  const actions =
    evidence.artifactId || recordingButton ? (
      <>
        {recordingButton}
        {evidence.artifactId ? (
          <EvidenceActionButtons
            artifactId={evidence.artifactId}
            onEdit={onEditEvidence}
          />
        ) : null}
      </>
    ) : undefined;

  if (isEditing && evidence.artifactId) {
    return (
      <EvidenceDescriptionEditor
        key={`evidence-${index}`}
        artifactId={evidence.artifactId}
        initialValue={evidence.explanation}
        onCancel={onCancelEdit}
      />
    );
  }

  if (evidence.artifactId && artifactsCtx) {
    const artifact =
      artifactsCtx.getArtifactById(evidence.artifactId) ??
      artifactsCtx.getArtifactByRuntimeId(evidence.artifactId);

    if (artifact) {
      return (
        <EvidenceCardWithArtifact
          key={`evidence-${index}`}
          artifact={artifact}
          explanation={evidence.explanation}
          index={index}
          defaultExpanded={index === 0}
          actions={actions}
        />
      );
    }
  }

  const isHttpEvidence = evidence.type?.toUpperCase() === 'HTTP';

  if (isHttpEvidence && evidence.toolExecutionHarId) {
    return (
      <EvidenceCardWithHar
        key={`evidence-${index}`}
        toolExecutionHarId={evidence.toolExecutionHarId}
        redirectHarIds={evidence.redirectHarIds}
        explanation={evidence.explanation}
        index={index}
        defaultExpanded
        actions={actions}
      />
    );
  }

  const protocolCard = renderProtocolEvidenceCard({
    evidence,
    index,
    actions,
  });

  if (protocolCard) {
    return protocolCard;
  }

  if (evidence.data) {
    return (
      <EvidenceCardWithData
        key={`evidence-${index}`}
        explanation={evidence.explanation}
        data={evidence.data}
        actions={actions}
      />
    );
  }

  if (evidence.content) {
    return (
      <EvidenceCard
        key={`evidence-${index}`}
        evidence={evidence.content}
        explanation={evidence.explanation}
        index={index}
        defaultExpanded={index === 0}
        actions={actions}
      />
    );
  }

  return (
    <EvidenceCardMissing
      key={`evidence-${index}`}
      explanation={evidence.explanation}
      actions={actions}
    />
  );
}

type EvidenceSectionProps = {
  isExpanded: boolean;
  onToggle: () => void;
  evidences: TraceEvidence[] | undefined;
  hasEvidence: boolean;
  noEvidenceLabel: string;
  evidenceCountLabel: string | undefined;
};

function EvidenceSection({
  isExpanded,
  onToggle,
  evidences,
  hasEvidence,
  noEvidenceLabel,
  evidenceCountLabel,
}: EvidenceSectionProps) {
  const t = useTranslations('designSystem.executions');
  const [editingArtifactId, setEditingArtifactId] = useState<string | null>(
    null,
  );
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const handleEditEvidence = (artifactId: string) => {
    setEditingArtifactId(artifactId);
  };

  const handleCancelEdit = () => {
    setEditingArtifactId(null);
  };

  const handleHeaderMouseEnter = () => {
    setIsHeaderHovered(true);
  };

  const handleHeaderMouseLeave = () => {
    setIsHeaderHovered(false);
  };

  return (
    <Collapsible.Root open={isExpanded} onOpenChange={() => onToggle()}>
      <Box
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        overflow="hidden"
        width="100%"
      >
        <Collapsible.Trigger asChild>
          <HStack
            gap={2}
            px={3}
            py={2}
            bg="bg.subtle"
            justifyContent="space-between"
            cursor="pointer"
            _hover={{ bg: 'bg.muted' }}
            onMouseEnter={handleHeaderMouseEnter}
            onMouseLeave={handleHeaderMouseLeave}
          >
            <HStack gap={2} alignItems="center">
              <SectionHeaderIcon
                icon={<FileCodeIcon size={14} />}
                isExpanded={isExpanded}
                isHovered={isHeaderHovered}
              />
              <Text fontSize="sm" fontWeight="medium" color="fg.default">
                {t('evidences')}
              </Text>
            </HStack>
            <HStack gap={1.5} alignItems="center">
              {evidenceCountLabel && (
                <Text fontSize="xs" color="fg.muted">
                  {evidenceCountLabel}
                </Text>
              )}
            </HStack>
          </HStack>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Box px={3} py={3}>
            {hasEvidence && evidences ? (
              <EvidenceList
                evidences={evidences}
                editingArtifactId={editingArtifactId}
                onEditEvidence={handleEditEvidence}
                onCancelEdit={handleCancelEdit}
              />
            ) : (
              <HStack gap={2} py={3} justifyContent="center" color="fg.muted">
                <FileQuestionMarkIcon size={16} />
                <Text fontSize="sm" color="fg.muted">
                  {noEvidenceLabel}
                </Text>
              </HStack>
            )}
          </Box>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}

const DEFAULT_EXPANDED_SECTIONS: TraceExpandedSections = {
  signal: true,
  analysis: true,
  evidence: true,
  blocked: true,
};

export const TraceSimplifiedView = forwardRef<
  TraceSimplifiedViewHandle,
  TraceSimplifiedViewProps
>(function TraceSimplifiedView(
  {
    signal,
    metadata,
    agentName: _agentName,
    maxHeight = '100%',
    isLoading = false,
    status = 'completed',
    expandedSections: controlledExpandedSections,
    onExpandedSectionsChange,
    disableDetailedView,
  },
  ref,
) {
  const t = useTranslations('designSystem.executions');
  const agentAnalyzingLabel = t('summaryView.agentAnalyzing');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Expose scroll element via ref handle
  useImperativeHandle(ref, () => ({
    getScrollElement: () => scrollRef.current,
  }));
  const hasSignal = signal?.description || signal?.analysis || signal?.category;
  const confidenceReason = metadata?.confidenceReason;
  const conclusion = metadata?.conclusion;
  const analysis = metadata?.analysis;
  const summaryContent = conclusion ?? analysis;
  const summarySectionTitle = conclusion
    ? t('summaryView.conclusion')
    : t('summaryView.analysis');
  const hasConfidence = Boolean(metadata?.confidence);
  const hasSignalLabelShift = Boolean(
    metadata?.previousSignalLabel && metadata?.newSignalLabel,
  );
  const hasSummaryContent = Boolean(summaryContent);
  const hasEvidence = metadata?.evidences && metadata.evidences.length > 0;
  const hasSkipReason = metadata?.skipAttackReason;
  const confidenceColorSchemeMap: Record<TraceConfidence, string> = {
    high: 'green',
    medium: 'yellow',
    low: 'gray',
  };
  const confidenceLabelMap: Record<TraceConfidence, string> = {
    high: t('summaryView.confidenceHigh'),
    medium: t('summaryView.confidenceMedium'),
    low: t('summaryView.confidenceLow'),
  };
  const blockedExecutionCopy = getBlockedExecutionCopy({
    skipAttackReasonType: metadata?.skipAttackReasonType,
    t: (key) => t(key),
  });
  const hasAnyContent =
    hasSignal ||
    hasSummaryContent ||
    hasConfidence ||
    hasEvidence ||
    hasSkipReason;
  const signalLabelBadge =
    hasSignalLabelShift &&
    metadata?.previousSignalLabel &&
    metadata?.newSignalLabel ? (
      <HStack gap={1} alignItems="center">
        {renderSignalLabelBadge(metadata.previousSignalLabel)}
        <Box color="fg.muted" display="flex" alignItems="center">
          <ArrowRight02Icon size={14} />
        </Box>
        {renderSignalLabelBadge(metadata.newSignalLabel)}
      </HStack>
    ) : signal?.status ? (
      <SignalStatusBadge status={signal.status} />
    ) : null;
  const confidenceBadgeContent = metadata?.confidence ? (
    <Tooltip title={confidenceReason} maxW="360px" disabled={!confidenceReason}>
      <Badge
        size="sm"
        variant="outline"
        colorScheme={confidenceColorSchemeMap[metadata.confidence]}
      >
        {t('summaryView.confidence')}:&nbsp;
        {confidenceLabelMap[metadata.confidence]}
      </Badge>
    </Tooltip>
  ) : undefined;
  // Section expand states - use controlled state if provided
  const [internalExpandedSections, setInternalExpandedSections] = useState<
    Record<string, boolean>
  >(controlledExpandedSections ?? DEFAULT_EXPANDED_SECTIONS);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  // Derive actual state
  const expandedSections =
    controlledExpandedSections ?? internalExpandedSections;
  const toggleSection = useCallback(
    (section: string) => {
      const newSections = {
        ...expandedSections,
        [section]: !expandedSections[section],
      };

      if (onExpandedSectionsChange) {
        onExpandedSectionsChange(newSections);
      } else {
        setInternalExpandedSections(newSections);
      }
    },
    [expandedSections, onExpandedSectionsChange],
  );
  const handleSectionHeaderMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setHoveredSection(event.currentTarget.dataset.section ?? null);
  };

  const handleSectionHeaderMouseLeave = () => {
    setHoveredSection(null);
  };

  if (isLoading) {
    return (
      <Box
        h={maxHeight}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="fg.muted" fontSize="sm">
          {t('summaryView.loading')}
        </Text>
      </Box>
    );
  }

  return (
    <Box h={maxHeight} w="full" overflow="hidden" p={4}>
      <VStack gap={0} h="full" alignItems="stretch">
        <Box flexShrink={0} pb={2}>
          <TraceViewHeader
            status={status}
            variant="summary"
            skipAttackReasonType={metadata?.skipAttackReasonType}
            disableDetailedView={
              disableDetailedView || Boolean(metadata?.skipAttackReason)
            }
          />
        </Box>

        <Box flex={1} overflow="hidden" pt={2} minH={0} display="flex" w="full">
          {!hasAnyContent ? (
            <Box
              h="full"
              w="full"
              flex="1"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              gap={3}
            >
              <AlertCircleIcon size={32} color={token('colors.fg.muted')} />
              <VStack gap={1} textAlign="center">
                <Text color="fg.default" fontSize="sm" fontWeight="medium">
                  {t('summaryView.emptyTitle')}
                </Text>
                <Text color="fg.muted" fontSize="xs">
                  {t('summaryView.emptyDescription')}
                </Text>
              </VStack>
            </Box>
          ) : (
            <Scroll
              ref={scrollRef}
              orientation="vertical"
              autoScroll={status === 'running'}
              showScrollbar="hover"
            >
              <VStack
                gap={3}
                pb={4}
                alignItems="stretch"
                w="full"
                paddingInline={2}
              >
                {hasSignal && (
                  <AccordionSection
                    icon={<Target03Icon size={14} />}
                    title="Signal"
                    badge={
                      <HStack gap={1}>
                        {signal?.category && (
                          <Badge size="sm" variant="outline">
                            {signal.category
                              .split('_')
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase(),
                              )
                              .join(' ')}
                          </Badge>
                        )}
                        {signalLabelBadge}
                      </HStack>
                    }
                    isExpanded={expandedSections.signal}
                    onToggle={() => toggleSection('signal')}
                  >
                    <VStack gap={2} alignItems="stretch">
                      {signal?.description && (
                        <Box>
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            {t('signal.description')}
                          </Text>
                          <Text fontSize="sm" color="fg.default">
                            {signal.description}
                          </Text>
                        </Box>
                      )}
                      {signal?.analysis && (
                        <Box color="fg.default">
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            {t('summaryView.initialAnalysis')}
                          </Text>
                          <MarkdownPreview
                            source={signal.analysis}
                            fontSize={12}
                            compact
                          />
                        </Box>
                      )}
                    </VStack>
                  </AccordionSection>
                )}

                {hasSkipReason && metadata?.skipAttackReason && (
                  <AccordionSection
                    icon={
                      <CancelCircleIcon
                        size={14}
                        color={token('colors.fg.neutral.default')}
                      />
                    }
                    title={blockedExecutionCopy.title}
                    badge={
                      <Badge size="sm" variant="outline">
                        {blockedExecutionCopy.badge}
                      </Badge>
                    }
                    isExpanded={expandedSections.blocked}
                    onToggle={() => toggleSection('blocked')}
                  >
                    <Text fontSize="sm" color="fg.default" lineHeight="1.6">
                      {metadata.skipAttackReason}
                    </Text>
                  </AccordionSection>
                )}

                {!hasSkipReason && (
                  <Collapsible.Root
                    open={expandedSections.analysis}
                    onOpenChange={() => toggleSection('analysis')}
                  >
                    <Box
                      borderWidth="1px"
                      borderColor="border.default"
                      borderRadius="md"
                      overflow="hidden"
                      width="100%"
                    >
                      <Collapsible.Trigger asChild>
                        <HStack
                          gap={2}
                          px={3}
                          py={2}
                          bg="bg.subtle"
                          justifyContent="space-between"
                          cursor="pointer"
                          _hover={{ bg: 'bg.muted' }}
                          data-section="analysis"
                          onMouseEnter={handleSectionHeaderMouseEnter}
                          onMouseLeave={handleSectionHeaderMouseLeave}
                        >
                          <HStack gap={2} alignItems="center">
                            <SectionHeaderIcon
                              icon={<File01Icon size={14} />}
                              isExpanded={Boolean(expandedSections.analysis)}
                              isHovered={hoveredSection === 'analysis'}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="fg.default"
                            >
                              {summarySectionTitle}
                            </Text>
                          </HStack>
                          {confidenceBadgeContent}
                        </HStack>
                      </Collapsible.Trigger>

                      <Collapsible.Content>
                        <Box px={3} py={3}>
                          {hasSummaryContent ? (
                            <VStack gap={3} alignItems="stretch">
                              {summaryContent && (
                                <Box color="fg.default">
                                  <MarkdownPreview
                                    source={summaryContent}
                                    fontSize={12}
                                    compact
                                  />
                                </Box>
                              )}
                            </VStack>
                          ) : (
                            <HStack
                              gap={2}
                              py={3}
                              justifyContent="center"
                              color="fg.muted"
                            >
                              <FileQuestionMarkIcon size={16} />
                              <Text fontSize="sm" color="fg.muted">
                                {t('summaryView.noAnalysis')}
                              </Text>
                            </HStack>
                          )}
                        </Box>
                      </Collapsible.Content>
                    </Box>
                  </Collapsible.Root>
                )}

                {/* Evidence Section - Hide when blocked by guardrails */}
                {!hasSkipReason && (
                  <EvidenceSection
                    isExpanded={expandedSections.evidence}
                    onToggle={() => toggleSection('evidence')}
                    evidences={metadata?.evidences}
                    hasEvidence={Boolean(hasEvidence)}
                    noEvidenceLabel={t('summaryView.noEvidence')}
                    evidenceCountLabel={
                      hasEvidence && metadata?.evidences
                        ? t('summaryView.evidenceCount', {
                            count: metadata.evidences.length,
                          })
                        : undefined
                    }
                  />
                )}

                {/* Thinking indicator when trace is running - hide when blocked */}
                {status === 'running' && !hasSkipReason && (
                  <HStack
                    gap={2}
                    py={4}
                    justifyContent="center"
                    alignItems="center"
                    color="fg.muted"
                  >
                    <Box animation="spin 1s linear infinite" display="flex">
                      <Loading03Icon size={16} />
                    </Box>
                    <Text fontSize="sm" color="fg.muted">
                      {agentAnalyzingLabel}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Scroll>
          )}
        </Box>
      </VStack>
    </Box>
  );
});
